import React, { AnimationEvent, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TextField } from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import FormWithActionBar from '../FormWithActionBar/FormWithActionBar';
import { ControlsStringAssets, MessagesStringAssets } from '../../../assets/stringAssets';
import { IHyperLink } from '../../../dataObjects/models/digitalMedia/HyperLink';
import { ISaveHyperLinkRequest } from '../../../dataObjects/models/serviceRequests/hyperLink/SaveHyperLinkRequest';
import { RegularExpressions } from '../../../assets/regularExpressionAssets';

interface IHyperLinkFormValues {
  title: string;
  hyperLinkUrl: string;
  description: string;
}

// using 'yup', set up a schema for the form field values
const schema = yup.object().shape({
  title: yup
    .string()
    .required(ControlsStringAssets.hyperLinkTitleRequired),
  // hyperLinkUrl: yup
  //   .string()
  //   .url(ControlsStringAssets.hyperLinkHyperLinkUrlInvalid)
  //   .required(ControlsStringAssets.hyperLinkHyperLinkUrlRequired),
  hyperLinkUrl: yup
    .string()
    .matches(RegularExpressions.GenericUrl, ControlsStringAssets.hyperLinkHyperLinkUrlInvalid)
    .required(ControlsStringAssets.hyperLinkHyperLinkUrlRequired),
  description: yup
    .string()
    .required(ControlsStringAssets.hyperLinkDescriptionRequired),
});

export interface IHyperLinkFormProps extends PropsWithChildren<unknown> {
  /**
   * @property {IHyperLink} hyperLink The HyperLink details for the form (will have blank properties values if we're creating a new record)
   */
  hyperLink: IHyperLink,
  /**
   * @property {boolean} saveRequestInProgress Whether a save request is in progress
   */
  saveRequestInProgress?: boolean,
  /**
   * @property {(hyperLink: IHyperLink) => Promise<void>} onSubmit Method to call for submitting the form for a save operation
   */
  onSubmit: (hyperLink: ISaveHyperLinkRequest) => Promise<void>,
}

const HyperLinkForm: React.FC<IHyperLinkFormProps> = (props: IHyperLinkFormProps) => {
  HyperLinkForm.displayName = 'HyperLink Form';

  // whether to display console logs (displayConsoleLogs && console.log statements)
  const displayConsoleLogs: boolean = true;

  // get required arguments from props
  const { hyperLink, onSubmit } = props;

  // set up details for ReactHookForm
  const { control, formState: { errors }, getValues, handleSubmit, register, setValue } = useForm({
    defaultValues: {
      title: hyperLink.title,
      hyperLinkUrl: hyperLink.hyperLinkUrl,
      description: hyperLink.description
    },
    // mode: "onBlur",
    mode: "all",
    resolver: yupResolver(schema)
  });

  const { ref: titleReg, ...titleProps } = register("title", { required: true, minLength: 1 });
  const { ref: hyperLinkUrlReg, ...hyperLinkUrlProps } = register("hyperLinkUrl", { required: true });
  const { ref: descriptionReg, ...descriptionProps } = register("description", { required: true, minLength: 1 });

  // capture whether a save is currently being submitted
  const saveRequestInProgress: boolean = props.saveRequestInProgress ?? false;

  // state value indicating whether a save is in progress
  const [saveInProgress, setSaveInProgress] = useState<boolean>(saveRequestInProgress);

  // for capturing the current value of the Title field
  const [currentTitle, setCurrentTitle] = useState<string>(hyperLink.title);

  // for capturing the current value of the Description field
  const [currentDescription, setCurrentDescription] = useState<string>(hyperLink.description);

  const [currentHyperlinkUrlValue, setCurrentHyperlinkUrlValue] = useState<string>(hyperLink.hyperLinkUrl);

  const [isTitleAutoFilled, setIsTitleAutoFilled] = useState<boolean>(false);
  const [isDescriptionAutoFilled, setIsDescriptionAutoFilled] = useState<boolean>(false);
  const [isHyperlinkUrlAutoFilled, setIsHyperlinkUrlAutoFilled] = useState<boolean>(false);

  const [formIsValid, setFormIsValid] = useState<boolean>(false);

  // determines whether the name field value is valid
  const validTitleFieldValue: () => boolean = useCallback(
    () => {
      displayConsoleLogs && console.log(`%c HyperlinkLinkForm. In validTitleFieldValue(); currentTitle: ${currentTitle}`, 'background: #055; color: #fff');

      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (currentTitle !== undefined) {
        isFieldValueValid = currentTitle.trim().length > 0;
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isTitleAutoFilled;
      }

      return isFieldValueValid;
    },
    [currentTitle, isTitleAutoFilled]
  );

  // determines whether the description field value is valid
  const validDescriptionFieldValue: () => boolean = useCallback(
    () => {
      displayConsoleLogs && console.log(`%c HyperlinkLinkForm. In validDescriptionFieldValue(); currentDescription: ${currentDescription}`, 'background: #055; color: #fff');

      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (currentDescription !== undefined) {
        isFieldValueValid = currentDescription.trim().length > 0;
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isDescriptionAutoFilled;
      }

      return isFieldValueValid;
    },
    [currentDescription, isDescriptionAutoFilled]
  );

  // determines whether the hyperlink url field value is valid
  const validHyperlinkUrlFieldValue: () => boolean = useCallback(
    () => {
      displayConsoleLogs && console.log(`%c HyperlinkLinkForm. In validHyperlinkUrlFieldValue(); currentHyperlinkUrlValue: ${currentHyperlinkUrlValue}`, 'background: #055; color: #fff');

      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if ((currentHyperlinkUrlValue !== undefined) && (currentHyperlinkUrlValue.trim().length > 0)) {
        isFieldValueValid = RegularExpressions.GenericUrl.test(currentHyperlinkUrlValue);
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isHyperlinkUrlAutoFilled;
      }

      return isFieldValueValid;
    },
    [currentHyperlinkUrlValue, isHyperlinkUrlAutoFilled]
  );

  const allRequiredFieldValuesValid: () => boolean = useCallback(
    () => {
      let fieldValuesValid: boolean = false;

      displayConsoleLogs && console.log(`%c HyperlinkLinkForm. In allRequiredFieldValuesValid(); currentTitle: ${currentTitle}; currentDescription: ${currentDescription}; currentHyperlinkUrlValue: ${currentHyperlinkUrlValue}`, 'background: #055; color: #fff');

      // if there's a valid title, description, and hyperlinkUrl...
      if (validTitleFieldValue() && validDescriptionFieldValue() && validHyperlinkUrlFieldValue()) {
        fieldValuesValid = true;
      }

      return fieldValuesValid;
    },
    [validTitleFieldValue, validDescriptionFieldValue, validHyperlinkUrlFieldValue]
  );

  const onTitleAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c HyperlinkLinkForm. In onTitleAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c HyperlinkLinkForm. In onTitleAnimationStart(). event.animationTitle === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isTitleAutoFilled) {
            setIsTitleAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c HyperlinkLinkForm. In onTitleAnimationStart(); getValues('description'): ${getValues('description')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  const onDescriptionAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c HyperlinkLinkForm. In onDescriptionAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c HyperlinkLinkForm. In onDescriptionAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isDescriptionAutoFilled) {
            setIsDescriptionAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c HyperlinkLinkForm. In onDescriptionAnimationStart(); getValues('description'): ${getValues('description')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  const onHyperlinkUrlAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c HyperlinkForm. In onHyperlinkUrlAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c HyperlinkForm. In onHyperlinkUrlAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isHyperlinkUrlAutoFilled) {
            setIsHyperlinkUrlAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c HyperlinkForm. In onHyperlinkUrlAnimationStart(); getValues('socialMediaPostUrl'): ${getValues('description')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  useEffect(() => {
    // let isFormValid: boolean = isValid && allRequiredFieldValuesValid();
    let isFormValid: boolean = allRequiredFieldValuesValid();

    displayConsoleLogs && console.log(`%c HyperlinkLinkForm. useEffect for [allRequiredFieldValuesValid]; isFormValid: ${isFormValid}`, 'background: #055; color: #fff');

    setFormIsValid(isFormValid);
  }, [allRequiredFieldValuesValid]);

  // useEffect hook for setting the 'saveInProgress' local state based on whether a save is currently in progress
  useEffect(() => {
    setSaveInProgress(saveRequestInProgress);
  }, [saveRequestInProgress]);

  // handles a save/submit request from the form
  const handleSaveSubmit = async (data: IHyperLinkFormValues) => {

    setSaveInProgress(true);

    // fill in title & description of the HyperLink object passed in
    hyperLink.title = data.title;
    hyperLink.hyperLinkUrl = data.hyperLinkUrl;
    hyperLink.description = data.description;

    // prepare a request for saving, starting with just the videoLink
    let saveHyperLinkRequest: ISaveHyperLinkRequest = {
      hyperLink: hyperLink
    };

    // call the onSubmit handler passed in, supplying the HyperLink object
    await onSubmit(saveHyperLinkRequest);
  }

  // present the form
  return (
    <>
      <FormWithActionBar
        onSubmit={handleSubmit(handleSaveSubmit)}
        actionInProgress={saveInProgress}
        actionInProgressLabel={MessagesStringAssets.hyperLink_SaveRequested}
        formIsValid={formIsValid}
      >
        <TextField
          inputRef={titleReg}
          {...titleProps}
          name="title"
          autoFocus
          label={ControlsStringAssets.hyperLinkTitleLabel}
          margin='normal'
          fullWidth
          error={!!errors.title}
          helperText={errors?.title?.message}
          slotProps={{
            inputLabel: {
              required: true // this will cause an asterisk ('*') to appear at the end of the label text
            },
            input: {
              onAnimationStart: onTitleAnimationStart()
            }
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setCurrentTitle(e.target.value);
          }}
        />

        <TextField
          inputRef={hyperLinkUrlReg}
          {...hyperLinkUrlProps}
          label={ControlsStringAssets.hyperLinkHyperLinkUrlLabel}
          margin='normal'
          fullWidth
          multiline={true}
          minRows={3}
          maxRows={5}
          error={!!errors.hyperLinkUrl}
          helperText={errors?.hyperLinkUrl?.message}
          slotProps={{
            inputLabel: {
              required: true // this will cause an asterisk ('*') to appear at the end of the label text
            },
            input: {
              onAnimationStart: onHyperlinkUrlAnimationStart()
            }
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setCurrentHyperlinkUrlValue(e.target.value);
          }}
        />

        <TextField
          inputRef={descriptionReg}
          {...descriptionProps}
          label={ControlsStringAssets.hyperLinkDescriptionLabel}
          margin='normal'
          fullWidth
          multiline={true}
          minRows={3}
          maxRows={5}
          error={!!errors.description}
          helperText={errors?.description?.message}
          slotProps={{
            inputLabel: {
              required: true // this will cause an asterisk ('*') to appear at the end of the label text
            },
            input: {
              onAnimationStart: onDescriptionAnimationStart()
            }
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setCurrentDescription(e.target.value);
          }}
        />

      </FormWithActionBar>
    </>

  );
}

export default HyperLinkForm;